/**
 * "Disponibilità squad": quanti atleti sono realmente utilizzabili oggi.
 *
 * Le quattro categorie del backend si escludono a vicenda, in ordine di
 * priorità: infortunati > a rischio > senza risposta > disponibili.
 */
import { FC } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { IActiveInjurySummary } from "../../../../models/injury";
import { InjuryBadge } from "../../../../shared/InjuryBadge/InjuryBadge";
import Typhography from "../../../../shared/Typhography/Typhography";
import { useAvailabilityCard } from "./useAvailabilityCard";
import style from "./AvailabilityCard.module.scss";

export const AvailabilityCard: FC<{
  teamId?: string;
  injuriesQuery: UseQueryResult<IActiveInjurySummary[], Error>;
}> = ({ teamId, injuriesQuery }) => {
  const { pulse, totale, isLoading, nessunaCompilazione, tuttiDisponibili } =
    useAvailabilityCard(teamId);

  const infortuni = injuriesQuery.data ?? [];

  return (
    <div className={style.container}>
      <p className={style.title}>Disponibilità squad</p>

      {/* Rosa vuota: "0/0 disponibili" non dice niente. */}
      {!isLoading && pulse && totale === 0 && (
        <Typhography size="regular-12" className={style.note}>
          Nessun atleta in rosa.
        </Typhography>
      )}

      <div className={style.row} hidden={Boolean(pulse) && totale === 0}>
        <div className={style.figure}>
          <span className={style.value}>
            {isLoading || !pulse ? "—" : `${pulse.available}/${totale}`}
          </span>
          <span className={style.caption}>disponibili</span>
        </div>

        {pulse && (
          <div className={style.breakdown}>
            {pulse.injured > 0 && (
              <span className={`${style.chip} ${style.chipInjured}`}>
                {pulse.injured} infortunat{pulse.injured === 1 ? "o" : "i"}
              </span>
            )}
            {pulse.atRisk > 0 && (
              <span className={`${style.chip} ${style.chipAtRisk}`}>
                {pulse.atRisk} a rischio
              </span>
            )}
            {pulse.noResponse > 0 && (
              <span className={`${style.chip} ${style.chipNoResponse}`}>
                {pulse.noResponse} senza risposta
              </span>
            )}
          </div>
        )}

        {tuttiDisponibili && (
          <span className={style.allGood}>✓ Tutti disponibili</span>
        )}

        {infortuni.length > 0 && (
          <div className={style.injuries}>
            <span className={style.injuriesLabel}>
              {infortuni.length} infortunat
              {infortuni.length === 1 ? "o" : "i"}
            </span>
            <div className={style.injuriesBadges}>
              {infortuni.map((inj) => (
                <InjuryBadge key={inj.injuryId} size="sm" injury={inj} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Il caso che prima veniva letto come "tutto a posto". */}
      {nessunaCompilazione && (
        <Typhography size="regular-12" className={style.note}>
          Nessun atleta ha compilato il questionario oggi: la disponibilità non
          è ancora valutabile.
        </Typhography>
      )}
    </div>
  );
};
